import { useEffect } from 'react'
import { useDispatch } from 'react-redux'

import { Box, CircularProgress } from '@material-ui/core'
import clsx from 'clsx'
import { FormattedMessage, useIntl } from 'react-intl'
import moment from 'moment'
import {
  checkSISDataImportThunk,
  enableAutoSyncThunk,
  getConnectedSISThunk,
} from 'app/store/importExternalSource/importExternalSourceReducers'
import { useUserProfile } from 'commons/useUserProfile'
import {
  setIsOpenImportProgressDialog,
  useImportExternalSourceData,
} from 'app/store/importExternalSource'
import DefaultSwitch from 'app/components/switch/DefaultSwitch'
import MessageAlert from 'app/components/MessageAlert'
import { SECONDARY } from 'app/constants/button'
import ContainedButton from 'app/componentV4/Button/ContainedButton'

import DialogImport from '../DialogImport'
import ImportProgressDialog from '../ImportProgressDialog'
import ImportedSubscribersResultDialog from '../ImportedSubscribersResultDialog'
import CircularProgressWithLabel from '../../LoadingProgress/CircularProgressWithLabel'
import {
  SCHOOL_IMPORT_STATUS_CANSEL,
  SCHOOL_IMPORT_STATUS_COMPLETED,
  SCHOOL_IMPORT_STATUS_FAILED,
} from '../../constansts'

import useStyles from './styles'

const ConnectedSIS = () => {
  const dispatch = useDispatch()
  const classes = useStyles()
  const { formatMessage } = useIntl()
  const userData = useUserProfile()

  const {
    connectedSIS,
    getStatus,
    importSISStatus,
    checkImportData,
    checkImportStatus,
    isListenSocketSchoolImport,
    schoolImportProcessStatusData,
    isClickedImportBtn,
    enableAutoSyncLoading,
  } = useImportExternalSourceData()

  const {
    id: schoolId,
    sis: { name: sisName, logo } = {},
    syncEnabled,
    lastImportDate,
  } = connectedSIS
  const { status } = schoolImportProcessStatusData
  const { isInProcess, userId, importStatus } = checkImportData
  const { id } = userData

  const wrongMessage = formatMessage({ id: 'wrong.msg' })

  const showSnackbar = (msg) => {
    const secondsInmillisec = 10000
    dispatch(
      MessageAlert.showError({ message: msg, duration: secondsInmillisec }),
    )
  }

  useEffect(() => {
    if (checkImportStatus === 'failed') {
      showSnackbar(wrongMessage)
    } else if (
      checkImportStatus === 'success' &&
      isInProcess &&
      userId !== id &&
      isClickedImportBtn
    ) {
      showSnackbar(
        formatMessage({
          id: 'subscribers.import.importSISInProcess.errorMessage',
        }),
      )
    }
  }, [checkImportStatus, isInProcess, userId, id, isClickedImportBtn])

  const getSyncDate = (date) => {
    if (!moment(date).isValid()) {
      return 'Unknown'
    }
    const syncDate = moment(date)
    const day = syncDate.date()
    return `${day}st of ${syncDate.format('MMM, YYYY')}`
  }

  useEffect(() => {
    if (
      importSISStatus === 'failed' ||
      status === SCHOOL_IMPORT_STATUS_FAILED
    ) {
      showSnackbar(wrongMessage)
    }
  }, [importSISStatus, status])

  useEffect(() => {
    if (!schoolId) {
      dispatch(
        getConnectedSISThunk({
          errorMessage: wrongMessage,
        }),
      )
    }
    if (
      !isListenSocketSchoolImport &&
      status !== SCHOOL_IMPORT_STATUS_COMPLETED
    ) {
      dispatch(checkSISDataImportThunk({ errorMessage: wrongMessage }))
    }
  }, [isListenSocketSchoolImport, status])

  const openImportDialog = () => {
    dispatch(
      checkSISDataImportThunk({
        schoolId,
        errorMessage: wrongMessage,
        isClickedImportBtn: true,
      }),
    )
  }

  const openImportProgressDialog = () => {
    dispatch(setIsOpenImportProgressDialog(true))
  }

  const handleOnClickImportBtn = () => {
    if (isListenSocketSchoolImport) {
      openImportProgressDialog()
    } else {
      openImportDialog()
    }
  }

  const enableAutoSync = (e) => {
    const { checked } = e.target
    const data = { enable: checked, schoolId }
    dispatch(enableAutoSyncThunk({ errorMessage: wrongMessage, data }))
  }

  if (getStatus === 'loading' || getStatus === 'idle')
    return (
      <Box
        display={'flex'}
        justifyContent={'center'}
        alignItems={'center'}
        height={'100%'}
      >
        <CircularProgressWithLabel size={50} value={0} />
      </Box>
    )

  if (getStatus === 'success' && !schoolId) {
    return (
      <Box
        component={'p'}
        fontSize={30}
        display={'flex'}
        justifyContent={'center'}
        alignItems={'center'}
        height={'100%'}
      >
        <FormattedMessage id="subscribers.importExternalSource.noSIS.text" />
      </Box>
    )
  }

  if (getStatus === 'failed') return

  const isLoadingImportBtn =
    isListenSocketSchoolImport ||
    (checkImportStatus === 'loading' && isClickedImportBtn)

  const isCancellationProcess = importStatus === SCHOOL_IMPORT_STATUS_CANSEL

  return (
    <>
      <Box mt={'71px'} px={'28px'}>
        <p className={classes.title}>
          <FormattedMessage id="subscribers.importExternalSource.tab.title" />
        </p>
        <Box
          display="flex"
          alignItems="center"
          className={classes.connectedSISWrap}
        >
          <Box
            display="flex"
            alignItems="center"
            flexWrap="wrap"
            sx={{
              justifyContent: {
                xs: 'center',
                sm: 'space-between',
              },
              flex: { md: 1 },
              gap: 10,
            }}
          >
            <Box display="flex" alignItems="center" height={'100%'}>
              {!!logo && (
                <img className={classes.SISLogo} alt="SIS logo" src={logo} />
              )}
              <p className={classes.SISName}>{sisName}</p>
            </Box>
            <Box display="flex" alignItems={'center'}>
              {enableAutoSyncLoading ? (
                <CircularProgressWithLabel size={30} value={0} />
              ) : (
                <DefaultSwitch
                  checked={syncEnabled}
                  onChange={enableAutoSync}
                />
              )}
              <Box
                display="flex"
                flexDirection="column"
                justifyContent="space-between"
                ml={2}
                height={'100%'}
              >
                <p className={classes.infoTitle}>
                  <FormattedMessage id="subscribers.importExternalSource.connectedSISInfo.text1" />
                </p>
                <p className={classes.syncInfoText}>
                  {getSyncDate(lastImportDate)}
                </p>
              </Box>
            </Box>
          </Box>
          <Box display="flex" justifyContent="flex-end" flex={1}>
            <ContainedButton
              size="small"
              color={SECONDARY}
              onClick={handleOnClickImportBtn}
              className={clsx('h-36 text-14 whitespace-nowrap', {
                [classes.inProcessImportBtn]: isLoadingImportBtn,
              })}
            >
              {isLoadingImportBtn && (
                <CircularProgress
                  size={15}
                  className={classes.circularProgress}
                />
              )}
              {isListenSocketSchoolImport ? (
                <FormattedMessage
                  id={
                    isCancellationProcess
                      ? 'subscribers.import.progressBar.text8'
                      : 'subscribers.importExternalSource.importButton.label2'
                  }
                />
              ) : (
                <FormattedMessage id="subscribers.importExternalSource.importButton.label" />
              )}
            </ContainedButton>
          </Box>
        </Box>
      </Box>
      <ImportProgressDialog />
      <ImportedSubscribersResultDialog />
      <DialogImport />
    </>
  )
}

export default ConnectedSIS
